import React, { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';

/* ---------------------------------------------------------------------------
   Shared building blocks for the campaign calculators (Concierge savings,
   Prototype scope). One card, two halves: a light input panel on the left and
   a solid accent result panel on the right, so both configurators read as the
   same product.
--------------------------------------------------------------------------- */

// Warm eyebrow tone from the reference design. Fixed rather than accent-bound,
// so it stays distinct from the accent the rest of the card is built around.
const EYEBROW_COLOR = '#F0532A';

// The sitewide h4/p rules are unlayered CSS and beat Tailwind's layered
// utilities, so the title's size is set inline (in the brand Avegra) and the
// card's text blocks are divs, not <p>.
const CALC_TITLE_STYLE = {
  fontFamily: '"Avegra", sans-serif',
  fontWeight: 400,
  letterSpacing: 0,
  fontSize: 'clamp(26px, 1.4vw + 16px, 36px)',
  lineHeight: 1.08,
  marginTop: 0,
};

/* Eases between the previous and the next value whenever the target changes,
   so totals visibly move instead of snapping. Plain rAF — a spring per digit
   would be more machinery than the effect is worth. */
export const AnimatedValue = ({ value, format, className, style }) => {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return undefined;
    // Reduced motion still routes through rAF rather than a synchronous
    // setState in the effect body: a zero duration lands on `to` in the very
    // first frame, which is indistinguishable from snapping.
    const prefersReduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const duration = prefersReduced ? 0 : 520;
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const p = duration === 0 ? 1 : Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      fromRef.current = to;
    };
  }, [value]);

  return <span className={className} style={style}>{format ? format(display) : display}</span>;
};

export const CalcCard = ({ left, right, color, leftClassName = '', rightClassName = '', className = '' }) => (
  <div
    className={`w-full rounded-[24px] md:rounded-[28px] overflow-hidden border border-border-color bg-bg-color font-['Inter',sans-serif] shadow-[0_30px_60px_-40px_rgba(0,0,0,0.35)] grid grid-cols-1 ${className}`}
  >
    <div className={`p-6 sm:p-8 md:p-10 ${leftClassName}`}>{left}</div>
    <div className={`p-6 sm:p-8 md:p-10 text-white ${rightClassName}`} style={{ backgroundColor: color }}>
      {right}
    </div>
  </div>
);

export const CalcTitle = ({ children, className = '' }) => (
  <h4 className={`text-text-color ${className}`} style={CALC_TITLE_STYLE}>{children}</h4>
);

export const CalcIntro = ({ eyebrow, title, text }) => (
  <div className="pb-7 md:pb-8 mb-7 md:mb-8 border-b border-border-color">
    <div className="text-[11px] font-bold uppercase tracking-[0.22em] mb-3" style={{ color: EYEBROW_COLOR }}>
      {eyebrow}
    </div>
    <CalcTitle>{title}</CalcTitle>
    {text && (
      <div className="mt-3 text-sm md:text-[15px] font-light leading-relaxed text-text-muted">{text}</div>
    )}
  </div>
);

export const CalcEyebrow = ({ children, className = '' }) => (
  <div className={`text-[11px] font-bold uppercase tracking-[0.22em] ${className}`} style={{ color: EYEBROW_COLOR }}>
    {children}
  </div>
);

export const CalcSlider = ({ label, value, display, min, max, step = 1, onChange, color }) => {
  const id = useId();
  const p = Math.min(Math.max((value - min) / (max - min), 0), 1);
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <label htmlFor={id} className="text-sm md:text-[15px] font-semibold leading-snug text-text-color">
          {label}
        </label>
        <span className="text-lg md:text-xl font-black tabular-nums whitespace-nowrap leading-none" style={{ color }}>
          {display}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-valuetext={String(display)}
        className="calc-range"
        // The fill stops under the thumb's centre, not at the raw percentage:
        // the thumb travels 24px less than the track is wide.
        style={{ '--accent': color, '--fill': `calc(12px + (100% - 24px) * ${p})` }}
      />
    </div>
  );
};

export const ResultRow = ({ icon: Icon, title, sub, value }) => (
  <div className="flex items-center gap-4">
    {Icon && (
      <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
        <Icon size={16} strokeWidth={2} />
      </span>
    )}
    <div className="flex-1 min-w-0">
      <div className="text-[15px] md:text-base font-semibold leading-tight">{title}</div>
      {sub && (
        <div className="mt-1 text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-white/70 leading-snug">{sub}</div>
      )}
    </div>
    <span className="text-lg md:text-xl font-black tabular-nums whitespace-nowrap">{value}</span>
  </div>
);

export const PillButton = ({ children, onClick, color }) => (
  <button
    type="button"
    onClick={onClick}
    className="group w-full flex items-center justify-center gap-2 rounded-full bg-white px-6 py-4 text-sm md:text-[15px] font-semibold transition-transform duration-300 hover:scale-[1.01] active:scale-[0.99]"
    style={{ color }}
  >
    {children}
    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
  </button>
);
